import CourseHoles from 'components/form/course/CourseHoles'
import CourseTeeBoxes from 'components/form/course/CourseTeeBoxes'
import ConfirmCourseVersionSaveDialog from 'components/ui/ConfirmCourseVersionSaveDialog'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import CourseVersionSaveSuccessDialog from 'components/ui/CourseVersionSaveSuccessDialog'
import { COURSE_DATA_SOURCE, COURSE_PROVIDER } from 'config'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { clearVersionStatus, selectCourse } from 'store/course/slice'
import { createCourseVersion, getCourse } from 'store/course/thunks'
import styled from 'styled-components'
import CustomTooltip from 'components/ui/CustomTooltip'
import { compareData } from 'utils/compareData'
import { ClientRoute } from 'routes/client'
import { validateCourseHoles, validateCourseTeeBoxes } from 'store/course/utils'
import ErrorMessage from 'components/ui/ErrorMessage'
import ContactSupportTooltip from 'components/ui/ContactSupportTooltip'
import { selectAuth } from 'store/authentication/slice'
import { getConfiguration } from 'store/authentication/thunks'

interface ParamTypes extends ParamTypesBase {
  id: string
}

export default function CourseDataEditor(): JSX.Element {
  const { id: paramId } = useParams<ParamTypes>()
  const id = paramId || ''
  const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false)
  const [courseHoles, setCourseHoles] = useState<CourseHole[]>([])
  const [courseTeeBoxes, setCourseTeeBoxes] = useState<CourseTeeBox[]>([])
  const [courseDefaultTees, setCourseDefaultTees] = useState<CourseDefaultTees | null>(null)
  const [holeDataHasDuplicates, setHoleDataHasDuplicates] = useState<boolean>(false)
  const [holeValidationErrors, setHoleValidationErrors] = useState<string[]>([])
  const [teeBoxValidationErrors, setTeeBoxValidationErrors] = useState<string[]>([])
  const intl = useIntl()
  const dispatch = useDispatch()
  const { course, status, versionSaveStatus, versionZendeskStatus } = useSelector(selectCourse)

  const saveSucceeded = versionSaveStatus === 'succeeded' && versionZendeskStatus === 'succeeded'

  const { siteConfig } = useSelector(selectAuth)

  useEffect(() => {
    if (!siteConfig) {
      dispatch(getConfiguration())
    }
  }, [])

  const courseVersion = useMemo(() => {
    const versions = course.versions || []
    const officialVersion = versions.find((cv) => cv.isOfficial)
    return officialVersion || versions[0]
  }, [course])

  const isSaveButtonDisabled = useMemo(() => {
    const holesChanged = compareData(courseHoles, courseVersion?.holes)
    const teeBoxesChanged = compareData(courseTeeBoxes, courseVersion?.teeBoxes)
    return (!holesChanged && !teeBoxesChanged && !courseDefaultTees) || holeDataHasDuplicates
  }, [courseVersion, courseHoles, courseTeeBoxes, courseDefaultTees, holeDataHasDuplicates])

  useEffect(() => {
    const courseId = Number(id)
    if (isNaN(courseId)) {
      return
    }
    dispatch(getCourse(courseId))
  }, [id, dispatch])

  const sortTeeBoxes = (): CourseTeeBox[] => {
    if (courseTeeBoxes.length === 0 || !courseDefaultTees) {
      return courseTeeBoxes
    }
    const returnArray: CourseTeeBox[] = [...courseTeeBoxes]

    const { menIndex, womenIndex: tempWomenIndex } = courseDefaultTees
    const differentDefaultTeeBoxes = menIndex !== tempWomenIndex

    let womenIndex = tempWomenIndex

    const defaultMen = returnArray.splice(menIndex, 1)[0]

    if (menIndex < womenIndex) {
      womenIndex--
    }

    if (differentDefaultTeeBoxes) {
      const defaultWomen = returnArray.splice(womenIndex, 1)[0]
      returnArray.splice(1, 0, defaultMen)
      returnArray.push(defaultWomen)
    } else {
      returnArray.push(defaultMen)
    }

    return returnArray
  }

  const getValidationErrors = () => {
    return [...holeValidationErrors, ...teeBoxValidationErrors]
  }

  const syncHoles = (holes: CourseHole[]) => {
    if (holeValidationErrors) {
      setHoleValidationErrors([])
    }
    setCourseHoles(holes)
  }

  const syncTeeBoxes = (teeBoxes: CourseTeeBox[]) => {
    setCourseTeeBoxes(teeBoxes)
  }

  const syncDefaultTees = useCallback((defaultTees: CourseDefaultTees) => {
    setCourseDefaultTees(defaultTees)
  }, [])

  const validateHoles = () => {
    const { holeValidationErrorsArray } = validateCourseHoles(courseHoles, intl)
    setHoleValidationErrors(holeValidationErrorsArray)
    return holeValidationErrorsArray.length === 0 && !holeDataHasDuplicates
  }

  const validateTeeBoxes = () => {
    const { teeBoxValidationErrorsArray } = validateCourseTeeBoxes(courseTeeBoxes, intl)
    setTeeBoxValidationErrors(teeBoxValidationErrorsArray)
    return teeBoxValidationErrorsArray.length === 0
  }

  const handleSendButtonClick = () => {
    const isValidHoles = validateHoles()
    const isValidTeeBoxes = validateTeeBoxes()
    if (!isValidHoles || !isValidTeeBoxes) {
      return
    }
    setSaveDialogOpen(true)
  }

  const handleSubmit = () => {
    const sortedTeeBoxes = sortTeeBoxes()
    const data: CourseVersionDataPayload = {
      holes: courseHoles,
      teeBoxes: sortedTeeBoxes,
      source: COURSE_DATA_SOURCE.GGB_USER,
    }
    dispatch(createCourseVersion({ courseId: course.id, data }))
  }

  const handleSuccessClose = () => {
    setSaveDialogOpen(false)
    dispatch(clearVersionStatus())
  }

  const isCourseDataModifiable = () => {
    const providerAbbr = (course.provider?.abbreviation || COURSE_PROVIDER.GGB) as COURSE_PROVIDER
    return [COURSE_PROVIDER.MGB, COURSE_PROVIDER.SGL].includes(providerAbbr as COURSE_PROVIDER) === false
  }

  if (status === 'loading') {
    return (
      <LoadingContainer>
        <LoadingSpinnerGreen size={50} />
      </LoadingContainer>
    )
  }

  const renderSaveButton = () => {
    return (
      <>
        {getValidationErrors().length > 0 && (
          <ValidationErrorMessage>
            <ul>
              {getValidationErrors().map((error) => (
                <li>{error}</li>
              ))}
            </ul>
          </ValidationErrorMessage>
        )}
        <SendCourseDataButton onClick={handleSendButtonClick} disabled={isSaveButtonDisabled}>
          {intl.formatMessage({ id: 'courseEditor.sendCourseData' })}
        </SendCourseDataButton>
      </>
    )
  }

  const maybeRenderSaveButtonWithTooltip = () => {
    if (!isSaveButtonDisabled) {
      return renderSaveButton()
    }
    return (
      <CustomTooltip title={intl.formatMessage({ id: 'courseEditor.editDataToSend' })}>
        <span>{renderSaveButton()}</span>
      </CustomTooltip>
    )
  }

  const renderCourseName = () => {
    return (
      <ContactSupportTooltip title={intl.formatMessage({ id: 'courseEditor.courseNameTooltip' })} position="right">
        <CourseName>{course.name}</CourseName>
      </ContactSupportTooltip>
    )
  }

  return (
    <>
      {saveDialogOpen && (
        <ConfirmCourseVersionSaveDialog handleSubmit={handleSubmit} close={() => setSaveDialogOpen(false)} />
      )}
      {saveSucceeded && <CourseVersionSaveSuccessDialog close={handleSuccessClose} />}
      <Container>
        {renderCourseName()}
        <SectionTitle>{intl.formatMessage({ id: 'strings.courseData' })}</SectionTitle>
        <SectionDetails>{intl.formatMessage({ id: 'courseEditor.courseDataInfo' })}</SectionDetails>
        <CourseHoles
          course={course}
          version={courseVersion}
          syncHoles={syncHoles}
          isModifiable={isCourseDataModifiable()}
          syncDuplicateHolesStatus={setHoleDataHasDuplicates}
          isCourseEditorUser
        />
        <SectionTitle>{intl.formatMessage({ id: 'courseEditor.teeAndRatings' })}</SectionTitle>
        <SectionDetails>{intl.formatMessage({ id: 'courseEditor.teeAndRatingsInfo' })}</SectionDetails>
        <CourseTeeBoxes
          version={courseVersion}
          isModifiable={isCourseDataModifiable()}
          syncTeeBoxes={syncTeeBoxes}
          syncDefaultTees={syncDefaultTees}
          isCourseEditorUser
        />
        {maybeRenderSaveButtonWithTooltip()}
        <Divider />
        <SectionTitle>{intl.formatMessage({ id: 'courseEditor.gpsCoordinates' })}</SectionTitle>
        <SectionDetails>{intl.formatMessage({ id: 'courseEditor.gpsCoordinatesInfo' })}</SectionDetails>
        <Link to={ClientRoute.COURSE_EDITOR_GPS(course.id)} target="_blank">
          <OpenGpsEditor>{intl.formatMessage({ id: 'courseEditor.launchGpsEditor' })}</OpenGpsEditor>
        </Link>
      </Container>
    </>
  )
}

const Container = styled.div`
  width: 100%;
  padding: 50px 100px;
`

const CourseName = styled.h1`
  display: inline-block;
  font-size: 34px;
  font-weight: 800;
  line-height: 40px;
  color: ${(props) => props.theme.customPalette.darkGreen};
`

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  color: ${(props) => props.theme.customPalette.darkGreen};
  margin: 50px 0 20px;
`

const SectionDetails = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #404040;
`

const LoadingContainer = styled.div`
  margin-top: 100px;
`

const SendCourseDataButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 50px auto 30px;
  cursor: pointer;
  width: 288px;
  height: 53px;
  color: #fff;
  background-color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid ${(props) => props.theme.palette.primary.main};
  border-radius: 8px;
  font-weight: 700;

  &:hover {
    opacity: 0.75;
  }

  &:disabled {
    opacity: 0.2;
    cursor: not-allowed;
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin: 50px 0;
  background-color: ${(props) => props.theme.customPalette.lightGray};
`

const OpenGpsEditor = styled.button`
  cursor: pointer;
  width: 200px;
  height: 53px;
  background-color: #fff;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid ${(props) => props.theme.palette.primary.main};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  margin-top: 20px;

  &:hover {
    background-color: #fff;
    opacity: 0.75;
  }
`

const ValidationErrorMessage = styled(ErrorMessage)`
  margin: 20px auto -20px auto;
  width: fit-content;
`
